/**
 * Chargement de l'IFrame Player API de YouTube.
 *
 * Le script s'installe une seule fois par page et publie `window.YT` de façon
 * asynchrone, via un callback global qu'il appelle lui-même. On enveloppe ça
 * dans une promesse mémorisée : plusieurs composants peuvent l'attendre sans
 * jamais provoquer un second chargement.
 */

export type YTPlayer = {
  loadVideoById(opts: { videoId: string; startSeconds?: number }): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  playVideo(): void;
  pauseVideo(): void;
  stopVideo(): void;
  setVolume(volume: number): void;
  getCurrentTime(): number;
  getPlayerState(): number;
  destroy(): void;
};

type YTNamespace = {
  Player: new (
    el: HTMLElement | string,
    options: {
      videoId?: string;
      playerVars?: Record<string, string | number>;
      events?: {
        onReady?: () => void;
        onStateChange?: (e: { data: number }) => void;
        onError?: (e: { data: number }) => void;
      };
    },
  ) => YTPlayer;
  PlayerState: { ENDED: number; PLAYING: number; PAUSED: number; BUFFERING: number; CUED: number };
};

declare global {
  interface Window {
    YT?: YTNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let pending: Promise<YTNamespace> | undefined;

export function loadYouTubeApi(): Promise<YTNamespace> {
  if (window.YT?.Player) return Promise.resolve(window.YT);

  pending ??= new Promise<YTNamespace>((resolve, reject) => {
    // Le script appelle ce callback global quand il est prêt ; c'est le seul
    // signal fiable — l'événement `load` de la balise arrive trop tôt.
    window.onYouTubeIframeAPIReady = () => {
      if (window.YT?.Player) resolve(window.YT);
      else reject(new Error("API YouTube chargée mais inutilisable"));
    };

    const script = document.createElement("script");
    script.src = "https://www.youtube.com/iframe_api";
    script.async = true;
    script.onerror = () => reject(new Error("Impossible de charger le player YouTube"));
    document.head.appendChild(script);
  });

  return pending;
}

/** Codes d'erreur du player, traduits pour être montrés tels quels. */
export function youTubeErrorMessage(code: number): string {
  switch (code) {
    case 2:
      return "Lien YouTube invalide.";
    case 5:
      return "Ce lecteur ne peut pas jouer cette vidéo.";
    case 100:
      return "Vidéo introuvable ou supprimée.";
    case 101:
    case 150:
      return "Le propriétaire interdit de jouer cette vidéo en dehors de YouTube.";
    default:
      return "Le player YouTube a refusé cette vidéo.";
  }
}
